import PropTypes from 'prop-types';
import React from 'react';
import { useTheme } from 'styled-components';
import { Input, Textarea } from 'components/FormFields';
import { Icon, orange, tokens } from 'components/v3';

// Small layout pieces shared by Field Properties and Form Settings
// (design: .pf, .pf-section-label, .pf-divider, .pf-hint). Inputs are the
// Style Guide's FormFields.

export const ERROR_RED = '#ED4337';

export const Block = ({ children, tight }) => (
  <div style={{ marginBottom: tight ? 8 : 18 }}>{children}</div>
);

Block.propTypes = { children: PropTypes.node, tight: PropTypes.bool };
Block.defaultProps = { children: null, tight: false };

export const SectionLabel = ({ children, id }) => {
  const t = tokens(useTheme());
  return (
    <div
      id={id}
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '1px',
        textTransform: 'uppercase',
        color: t.faint,
        margin: '4px 0 12px',
      }}
    >
      {children}
    </div>
  );
};

SectionLabel.propTypes = { children: PropTypes.node, id: PropTypes.string };
SectionLabel.defaultProps = { children: null, id: undefined };

export const Divider = () => {
  const t = tokens(useTheme());
  return (
    <div
      role="separator"
      style={{ height: 1, background: t.border, margin: '18px 0' }}
    />
  );
};

export const Hint = ({ children, top }) => {
  const t = tokens(useTheme());
  return (
    <div
      style={{
        fontSize: 12,
        lineHeight: 1.55,
        color: t.faint,
        margin: `${top}px 0 12px`,
      }}
    >
      {children}
    </div>
  );
};

Hint.propTypes = { children: PropTypes.node, top: PropTypes.number };
Hint.defaultProps = { children: null, top: -8 };

export const Note = ({ children, icon }) => {
  const t = tokens(useTheme());
  return (
    <div
      style={{
        display: 'flex',
        gap: 10,
        alignItems: 'flex-start',
        padding: '12px 13px',
        marginBottom: 14,
        borderRadius: 10,
        background: orange(0.08),
        border: '1px solid ' + orange(0.25),
        fontSize: 12,
        lineHeight: 1.5,
        color: t.sub,
      }}
    >
      <Icon name={icon} size={16} color={orange(1)} />
      <span>{children}</span>
    </div>
  );
};

Note.propTypes = { children: PropTypes.node, icon: PropTypes.string };
Note.defaultProps = { children: null, icon: 'info' };

export const ErrorNote = ({ children }) =>
  children ? (
    <div
      role="alert"
      style={{
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        padding: '10px 12px',
        marginBottom: 16,
        borderRadius: 10,
        border: '1px solid rgba(237, 67, 55, 0.45)',
        background: 'rgba(237, 67, 55, 0.08)',
        color: ERROR_RED,
        fontSize: 13,
        fontWeight: 600,
      }}
    >
      <Icon name="triangle-alert" size={16} color="currentColor" />
      {children}
    </div>
  ) : null;

ErrorNote.propTypes = { children: PropTypes.node };
ErrorNote.defaultProps = { children: null };

// FormFields Input with its label, a matching accessible name and the
// error (red border + message).
export const TextField = ({ label, value, onChange, error, ...rest }) => (
  <Block>
    <Input
      label={label}
      aria-label={label}
      value={value == null ? '' : String(value)}
      onChange={(e) => onChange(e.target.value)}
      error={!!error}
      helperText={error || undefined}
      {...rest}
    />
  </Block>
);

TextField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
};

TextField.defaultProps = { value: '', error: null };

export const TextAreaField = ({ label, value, onChange, error, ...rest }) => (
  <Block>
    <Textarea
      label={label}
      aria-label={label}
      value={value == null ? '' : String(value)}
      onChange={(e) => onChange(e.target.value)}
      error={!!error}
      helperText={error || undefined}
      {...rest}
    />
  </Block>
);

TextAreaField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string,
};

TextAreaField.defaultProps = { value: '', error: null };

// A panel head: orange icon tile, title and a one-line subtitle.
export const PanelHead = ({ icon, title, sub }) => {
  const t = tokens(useTheme());
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '14px 18px',
        borderBottom: '1px solid ' + t.border,
      }}
    >
      <span
        style={{
          width: 32,
          height: 32,
          borderRadius: 8,
          background: orange(0.14),
          color: orange(1),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon name={icon} size={17} color="currentColor" />
      </span>
      <div style={{ minWidth: 0 }}>
        <h2
          style={{
            margin: 0,
            fontSize: 16,
            fontWeight: 600,
            color: t.text,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </h2>
        <div style={{ fontSize: 13, color: t.faint }}>{sub}</div>
      </div>
    </div>
  );
};

PanelHead.propTypes = {
  icon: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  sub: PropTypes.string.isRequired,
};

// Disables every control inside at once (archived forms).
export const ReadOnlyScope = ({ readOnly, children }) => (
  <fieldset
    disabled={readOnly}
    style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
  >
    {children}
  </fieldset>
);

ReadOnlyScope.propTypes = {
  readOnly: PropTypes.bool,
  children: PropTypes.node,
};
ReadOnlyScope.defaultProps = { readOnly: false, children: null };
